/**
 * Admin Marketing — tracked links and attribution.
 *
 *   /plays/<id>/links         GET list | POST mint (one per creator/placement)
 *   /plays/<id>/attribution   clicks -> users -> signups / listings / paid orders
 *   /attribution/sources      visits + signups by traffic source, 24h spike ratio
 *
 * Every number is computed server-side (ff-api service/acquisition.py and
 * service/marketing_attribution.py) and shown as given.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import { MARKETING_BASE, marketingKeys } from './keys';

export interface ApiTrackedLink {
  link_id: number;
  code: string;
  url: string;
  destination_url: string;
  active: boolean;
  click_count: number;
  last_clicked_at: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
}

export interface MintLinkPayload {
  destination_url: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
}

export interface ApiPlayAttribution {
  play_id: number;
  window_days?: number;
  attribution_window_hours?: number;
  clicks: number;
  clicks_without_session?: number;
  users?: number;
  users_via_session?: number;
  users_via_touch?: number;
  outcomes?: { signups: number; listings: number; orders: number };
  note?: string;
  error?: string;
}

export interface ApiTrafficSource {
  channel: string;
  source: string;
  sanctioned: boolean;
  visits: number;
  visits_24h: number;
  daily_avg_prior: number;
  spike_ratio: number | null;
  identified_users: number;
  signups: number;
}

export interface ApiTaggedCampaign {
  utm_id: string | null;
  utm_campaign: string | null;
  source: string;
  visits: number;
  signups: number;
  play_id?: number | null;
}

export interface ApiAttributionSources {
  window_days: number;
  visits: number;
  signups: number;
  sources: ApiTrafficSource[];
  campaigns: ApiTaggedCampaign[];
  note: string;
}

export const usePlayLinksQuery = (playId: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<{ items: ApiTrackedLink[] }>>({
    defaultErrorMessage: 'Could not load tracked links',
    endpoint: `${MARKETING_BASE}/plays/${playId}/links`,
  });
  return useQuery({
    queryKey: marketingKeys.plays.links(playId),
    queryFn: () => queryFn(),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data?.items ?? [],
  });
};

export const useMintPlayLinkMutation = (playId: number) => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<MintLinkPayload, AdminEnvelope<ApiTrackedLink>>({
    defaultErrorMessage: 'Could not create the link',
    endpoint: `${MARKETING_BASE}/plays/${playId}/links`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: marketingKeys.plays.links(playId) }),
  });
};

export const usePlayAttributionQuery = (playId: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPlayAttribution>>({
    defaultErrorMessage: 'Could not load attribution',
    endpoint: `${MARKETING_BASE}/plays/${playId}/attribution`,
  });
  return useQuery({
    queryKey: marketingKeys.plays.attribution(playId),
    queryFn: () => queryFn(),
    enabled,
    // A Mongo aggregation plus a business-DB join: not something to poll.
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export interface ApiPlayWhatsapp {
  broadcasts: {
    campaign_id: string;
    name: string | null;
    status: string | null;
    from_number: string | null;
    created_at: number | null;
    total: number;
    sent: number;
    failed: number;
    skipped: number;
    replies: number;
  }[];
  totals: { broadcasts: number; sent: number; failed: number; skipped: number; replies: number };
}

/** WhatsApp broadcasts tagged with this campaign. Counted server-side. */
export const usePlayWhatsappQuery = (playId: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiPlayWhatsapp>>({
    defaultErrorMessage: 'Could not load WhatsApp results',
    endpoint: `${MARKETING_BASE}/plays/${playId}/whatsapp`,
  });
  return useQuery({
    queryKey: marketingKeys.plays.whatsapp(playId),
    queryFn: () => queryFn(),
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useAttributionSourcesQuery = (days: number) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiAttributionSources>>({
    defaultErrorMessage: 'Could not load traffic sources',
    endpoint: `${MARKETING_BASE}/attribution/sources`,
  });
  return useQuery({
    queryKey: marketingKeys.attribution.sources(days),
    queryFn: () => queryFn({ days }),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};
